import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, map, mapTo, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { OrganisationId } from '../../../core/organisations/entities/organisation.entity';
import { isNotNullOrUndefined } from '../../../helpers/typed-reactive-filters.helper';
import {
    createDisplayMode,
    DisplayMode
} from '../../../overviews/generic-overview/components/display-settings-bar/entities/display-mode.entity';
import { SortInfo } from '../../../overviews/generic-overview/components/display-settings-bar/entities/sort-info.entity';
import { createSortInfo } from '../../../overviews/generic-overview/components/display-settings-bar/helpers/sort-info.helper';
import { PaginatedResults } from '../../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import { PaginationInfo } from '../../../overviews/generic-overview/components/pagination/entities/pagination-info.entity';
import { mapToPaginatedResult } from '../../../overviews/generic-overview/components/pagination/helpers/map-to-paginated-result.helper';
import { SearchState } from '../../../overviews/search-page/entities/search-page-state.entity';
import { ShApiCachingHelper } from '../../api/helpers/api-caching.helper';
import { ExportLocation } from '../../exports/entities/export-location.entity';
import { Group, UserGroupType } from '../../groups/entities/group.entity';
import { CustomHttpParamEncoder } from '../../http/entities/custom-http-param-encoder';
import { ShAbstractHttpService } from '../../http/services/http.abstract.service';
import { ShPaginationService } from '../../http/services/pagination.service';
import { Role } from '../../roles/entities/role.entity';
import { Zone } from '../../zones/entities/zone.entity';
import { PaginatedUser } from '../entities/paginated-user.class';
import { UserFunction, UserFunctions } from '../entities/user-functions.entity';
import { ApiUserPreferences, UserPreferences } from '../entities/user-preferences.entity';
import { User, UserDetails, UserId } from '../entities/user.entity';

@Injectable({ providedIn: 'root' })
export class UserService implements OnDestroy {
    private readonly defaultHeaders: HttpHeaders = new HttpHeaders();
    private readonly userEndpoint: string = 'v2/users';
    private readonly currentUserEndpoint: string = `${this.userEndpoint}/current`;
    private readonly currentUserFunctionsEndpoint: string = `${this.currentUserEndpoint}/functions`;
    private readonly currentUserPreferencesEndpoint: string = `${this.currentUserEndpoint}/preferences`;
    private readonly destroy$ = new Subject<void>();

    private readonly userFunctions$: BehaviorSubject<UserFunctions | null> = new BehaviorSubject<UserFunctions | null>(
        null
    );
    private functionsRefreshSubscription: Subscription;
    private loadingFunctions = false;

    private userPreferences$: Observable<UserPreferences>;

    private readonly user$: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);

    /* Zone is currently only auto-assigned when item is concept,
    but to avoid confusing users (see MED-1521) we will always filter this out */
    /* Ideally this information should come from the Engine as this is BL --> ENG-921 */
    protected autoAssignedGroupTypes: string[] = [UserGroupType.Zone, UserGroupType.Administrator];
    constructor(
        protected http: ShAbstractHttpService,
        protected apiCachingHelper: ShApiCachingHelper,
        protected paginationService: ShPaginationService
    ) {}

    // #region Users lists
    public getUserList(
        searchState: SearchState,
        organisationId?: OrganisationId,
        includeSystem: boolean = false
    ): Observable<PaginatedResults<PaginatedUser>> {
        const sortInfo: SortInfo = searchState.sortInfo;
        const paginationInfo: PaginationInfo = {
            page: searchState.page,
            resultsPerPage: searchState.pageSize
        };
        const startIndex: number = paginationInfo.page * paginationInfo.resultsPerPage;

        let params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() })
            .set('sort', sortInfo.field)
            .set('direction', sortInfo.direction)
            .set('includeSystem', includeSystem);

        if (organisationId) {
            params = params.set('organisationId', organisationId);
        }

        searchState.structuredQuery.Fields?.forEach(queryPart => {
            if (queryPart.values.length > 0) {
                let wildcardSearch = false;
                const field: string = queryPart.field.replace('*', () => {
                    wildcardSearch = true;
                    return '';
                });

                let value: string = queryPart.values.join('');
                if (wildcardSearch) {
                    value = `*${value}*`;
                }

                params = params.set(field, value);
            }
        });

        return this.paginationService
            .get<PaginatedUser>(
                this.userEndpoint,
                params,
                this.defaultHeaders,
                startIndex,
                paginationInfo.resultsPerPage
            )
            .pipe(
                map(results => ({
                    ...results,
                    Results: results.Results.map(user => new PaginatedUser(user))
                }))
            )
            .pipe(map(results => mapToPaginatedResult(results, paginationInfo.resultsPerPage)));
    }

    public getAllUsers(organisationId?: OrganisationId): Observable<User[]> {
        let params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set('includeSystem', true);
        if (organisationId) {
            params = params.set('organisationId', organisationId);
        }

        return this.paginationService.getAll<User>(this.userEndpoint, params);
    }

    public getUserById(id: UserId): Observable<User> {
        const request = `${this.userEndpoint}/${id}`;
        return this.http.get<User>(request);
    }

    public getAllUserRolesByUserId(id: UserId): Observable<Role[]> {
        const request = `${this.userEndpoint}/${id}/roles`;
        return this.paginationService.getAll<Role>(request).pipe(catchError(() => of([])));
    }

    public getAllUserZonesByUserId(id: UserId): Observable<Zone[]> {
        const request = `${this.userEndpoint}/${id}/zones`;
        return this.paginationService.getAll<Zone>(request).pipe(catchError(() => of([])));
    }

    public getAllUserGroupsByUserId(id: UserId): Observable<Group[]> {
        const request = `${this.userEndpoint}/${id}/groups`;
        return this.paginationService.getAll<Group>(request).pipe(catchError(() => of([])));
    }

    public getAllUserExportLocationsByUserId(id: UserId): Observable<ExportLocation[]> {
        const request = `${this.userEndpoint}/${id}/export-locations`;
        return this.paginationService.getAll<ExportLocation>(request).pipe(catchError(() => of([])));
    }

    // #endregion

    // #region User data

    public getCurrentUser(): Observable<User> {
        return this.user$.pipe(switchMap(user => (user ? of(user) : this.initUser())));
    }

    public initUser() {
        const cachedUser$: Observable<User> = this.apiCachingHelper.cachedApiCall(this.fetchUser()).pipe(
            tap(user => this.user$.next(user)),
            distinctUntilChanged()
        );
        return cachedUser$.pipe(take(1));
    }

    public clearCurrentUser(): void {
        this.userFunctions$.next(null);
    }

    public fetchUser(): Observable<User> {
        const request = `${this.currentUserEndpoint}`;
        return this.http.get<User>(request);
    }

    public saveUser(user: UserDetails): Observable<User> {
        return this.http.put<User>(this.currentUserEndpoint, user);
    }

    // #endregion

    // #region UserFunction data

    public getUserFunctions(): Observable<UserFunctions> {
        // don't return functions if user is null
        return this.getCurrentUser().pipe(
            isNotNullOrUndefined(),
            switchMap(() => this.userFunctions$),
            switchMap(userFunctions => (userFunctions ? of(userFunctions) : this.initFunctions())),
            isNotNullOrUndefined(),
            take(1)
        );
    }

    public getEnabledUserFunctions(): Observable<UserFunction[]> {
        return this.getUserFunctions().pipe(
            map(userFunctions => {
                const enabledUserFunctions: UserFunction[] = [];

                for (const key in userFunctions) {
                    if (userFunctions[key as UserFunction]) {
                        enabledUserFunctions.push(key as UserFunction);
                    }
                }

                return enabledUserFunctions;
            }),
            shareReplay(1)
        );
    }

    public fetchUserFunctions(): Observable<UserFunctions> {
        return this.http
            .get<string[]>(this.currentUserFunctionsEndpoint)
            .pipe(map(userFunctions => this.extractUserFunctions(userFunctions)));
    }

    public initFunctions(): Observable<UserFunctions> {
        if (!this.loadingFunctions) {
            this.loadingFunctions = true;

            const cachedUserFunctions$: Observable<UserFunctions> = this.apiCachingHelper.cachedApiCall(
                this.user$.pipe(
                    isNotNullOrUndefined(),
                    distinctUntilChanged((user1, user2) => user1.Id === user2.Id),
                    switchMap(() => this.fetchUserFunctions()),
                    shareReplay(1)
                )
            );

            // prevent multiple subscriptions being active
            if (this.functionsRefreshSubscription) {
                this.functionsRefreshSubscription.unsubscribe();
            }

            this.functionsRefreshSubscription = cachedUserFunctions$.subscribe(functions => {
                this.userFunctions$.next(functions);
                this.loadingFunctions = false;
            });

            return cachedUserFunctions$.pipe(take(1));
        }

        return this.userFunctions$.pipe(isNotNullOrUndefined());
    }

    public hasUserFunction(userFunction: UserFunction): Observable<boolean> {
        return this.getUserFunctions().pipe(map(functions => functions[userFunction] === true));
    }

    public hasUserFunctions(userFunctions: UserFunction[]): Observable<boolean> {
        const checkedUserFunction$: Observable<boolean>[] = userFunctions.map(userFunction =>
            this.hasUserFunction(userFunction).pipe(take(1))
        );

        return combineLatest(checkedUserFunction$).pipe(
            map(hasFunctions => hasFunctions.every(hasFunction => hasFunction))
        );
    }

    public hasAnyUserFunction(userFunctions: UserFunction[]): Observable<boolean> {
        const checkedUserFunction$: Observable<boolean>[] = userFunctions.map(userFunction =>
            this.hasUserFunction(userFunction).pipe(take(1))
        );

        return combineLatest(checkedUserFunction$).pipe(
            map(hasFunctions => hasFunctions.some(hasFunction => hasFunction))
        );
    }

    // #endregion

    // #region User Groups
    public getUserGroups(): Observable<Group[]> {
        return this.getCurrentUser().pipe(map(user => user.Groups));
    }

    public getAssignableUserGroups(): Observable<Group[]> {
        return this.getUserGroups().pipe(
            map(groups => groups.filter(group => !this.autoAssignedGroupTypes.includes(group.Type)))
        );
    }

    public getUserGroup(key: string): Observable<Group | null> {
        return this.getUserGroups().pipe(
            map(groups => groups.filter(group => group.Id === key)),
            map(groups => groups[0] || null)
        );
    }

    // #endregion

    // #region UserPreference data

    public initUserPreferences(): Observable<UserPreferences> {
        const cachedUserPreferences$: Observable<UserPreferences> = this.apiCachingHelper.cachedApiCall(
            this.fetchUserPreferences()
        );
        cachedUserPreferences$.subscribe(userPreferences => (this.userPreferences$ = of(userPreferences)));
        return cachedUserPreferences$.pipe(take(1));
    }

    public fetchUserPreferences(userId?: UserId): Observable<UserPreferences> {
        return this.http
            .get<ApiUserPreferences>(
                userId ? `${this.userEndpoint}/${userId}/preferences` : this.currentUserPreferencesEndpoint
            )
            .pipe(map(userPreferences => this.enhanceUserPreferences(userPreferences)));
    }

    public getUserPreferences(): Observable<UserPreferences> {
        return this.userPreferences$ ? this.userPreferences$ : this.initUserPreferences();
    }

    public saveUserPreferences(userPreferences: ApiUserPreferences, userId?: UserId): Observable<UserPreferences> {
        return this.http
            .post<ApiUserPreferences>(
                userId ? `${this.userEndpoint}/${userId}/preferences` : this.currentUserPreferencesEndpoint,
                userPreferences
            )
            .pipe(map(resultPreferences => this.enhanceUserPreferences(resultPreferences)));
    }

    private enhanceUserPreferences(userPreferences: ApiUserPreferences): UserPreferences {
        const defaultSort: SortInfo | null =
            // return null when server gives null is on purposed, since every page has its own DEFAULT_XYZ_MODE
            userPreferences.DefaultSortField && userPreferences.DefaultSortDirection
                ? createSortInfo(userPreferences.DefaultSortField, userPreferences.DefaultSortDirection)
                : null;

        const defaultDisplayMode: DisplayMode | null = userPreferences.DefaultDisplay
            ? // return null is on purposed, see above
              createDisplayMode(userPreferences.DefaultDisplay)
            : null;

        return {
            ...userPreferences,
            DefaultSort: defaultSort,
            DefaultDisplay: defaultDisplayMode
        };
    }

    // #endregion

    public createNewUser(userData: any): Observable<User> {
        return this.http.post(this.userEndpoint, userData);
    }

    // #region update user
    public updateUser(userData: any, userId: string): Observable<User> {
        const request = `${this.userEndpoint}/${userId}`;
        return this.http.put(request, userData);
    }

    public updateUserRoles(userId: string, roleIds: string[]): Observable<boolean> {
        const request = `${this.userEndpoint}/${userId}/roles`;
        return this.http.put(request, roleIds).pipe(mapTo(true));
    }

    // when PUTting, Engine expects an explicitly empty array, which does not always travel as param somehow. Do not remove fallback []
    public updateUserZones(userId: string, zoneIds: string[]): Observable<boolean> {
        const request = `${this.userEndpoint}/${userId}/zones`;
        return this.http.put(request, zoneIds ?? []).pipe(mapTo(true));
    }

    public updateUserGroups(userId: string, groupIds: string[]): Observable<boolean> {
        const request = `${this.userEndpoint}/${userId}/groups`;
        return this.http.put(request, groupIds).pipe(mapTo(true));
    }

    public updateUserExportLocations(userId: string, exportLocationIds: string[]): Observable<boolean> {
        const request = `${this.userEndpoint}/${userId}/export-locations`;
        return this.http.put(request, exportLocationIds ?? []).pipe(mapTo(true));
    }

    // #endregion

    // #region delete
    public deleteUser(userId: UserId | undefined): Observable<boolean> {
        const request = `${this.userEndpoint}/${userId}`;
        return this.http.delete(request).pipe(mapTo(true));
    }

    // #endregion

    // UserFunction utility
    private extractUserFunctions(userFunctions: string[] = []): UserFunctions {
        return Object.values(UserFunction).reduce(
            (tot, func) => ({
                ...tot,
                [func]: userFunctions.includes(func)
            }),
            {}
        ) as UserFunctions;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
